import type { RouteObject } from "react-router-dom";

import { EnvironmentDetailsLayout } from "@/routes/environments/details/EnvironmentDetailsLayout";

export type EnvironmentDetailsRouteHandle = {
    label?: string;
    /** When true, the route needs full height without ScrollArea wrapper (e.g., for LogViewer) */
    fullHeight?: boolean;
};

export function createEnvironmentDetailsRoute(): RouteObject {
    return {
        path: "details/:environmentId",
        // Eagerly loaded: lazy loading the layout causes @react-aria/interactions
        // to crash in happy-dom when the module initializes inside the router context.
        Component: EnvironmentDetailsLayout,
        children: [
            {
                index: true,
                lazy: () =>
                    import("@/routes/environments/details/EnvironmentDetailsPage").then((m) => ({
                        Component: m.EnvironmentDetailsPage,
                    })),
            },
            {
                path: "logs",
                lazy: () =>
                    import("@/routes/environments/details/EnvironmentLogsPage").then((m) => ({
                        Component: m.EnvironmentLogsPage,
                    })),
                handle: {
                    label: "Logs",
                    fullHeight: true,
                } satisfies EnvironmentDetailsRouteHandle,
            },
            {
                path: "services/:serviceId/logs",
                lazy: () =>
                    import("@/routes/environments/details/services/ServiceLogsPage").then((m) => ({
                        Component: m.ServiceLogsPage,
                    })),
                handle: {
                    label: "Service Logs",
                    fullHeight: true,
                } satisfies EnvironmentDetailsRouteHandle,
            },
            {
                path: "tasks",
                lazy: () =>
                    import("@/routes/environments/details/EnvironmentTaskRunsPage").then((m) => ({
                        Component: m.EnvironmentTaskRunsPage,
                    })),
                handle: {
                    label: "All Task Runs",
                } satisfies EnvironmentDetailsRouteHandle,
            },
            {
                path: "task/:taskId",
                lazy: () =>
                    import("@/routes/environments/details/EnvironmentTaskRunsPage").then((m) => ({
                        Component: m.EnvironmentTaskRunsPage,
                    })),
                handle: {
                    label: "Task Runs",
                } satisfies EnvironmentDetailsRouteHandle,
            },
            {
                path: "task/:taskId/run/:runId",
                lazy: () =>
                    import("@/routes/environments/details/EnvironmentTaskRunLogsPage").then((m) => ({
                        Component: m.EnvironmentTaskRunLogsPage,
                    })),
                handle: {
                    label: "Task Run Logs",
                    fullHeight: true,
                } satisfies EnvironmentDetailsRouteHandle,
            },
        ],
    };
}
